import styled from "@emotion/styled";
import type { StepColumnsType } from "@stepfinance/react";
import {
  ContextButton,
  formatBalance,
  getFirstWord,
  imageFromPlatformName,
  StepTable,
  TokenTag,
  URLIcon,
} from "@stepfinance/react";
import type { TablePaginationConfig } from "antd";
import type { FC } from "react";
import { useEffect, useState } from "react";

import { useEnrichedWallet } from "../../../contexts/enrichedWallet";
import { useLanguageContext } from "../../../contexts/language/language";
import { useMarkets } from "../../../contexts/market";
import type { MarginPlatforms, MarginPosition } from "../../../types/margin";
import { getMarginPositionIcon } from "../../../utils/margin";
import { getTokenBySymbol } from "../../../utils/tokenInfo";
import { isPaginationHidden, useLocalStorageState } from "../../../utils/utils";
import { IconTextContainer, PAGE_SIZE_OPTIONS, TokenIconText } from "../../shared";
import CellValue from "../../Table/CellValue";
import { ChevronCollapse } from "../ChevronCollapse/ChevronCollapse";

interface UnsettledBalance {
  kind: MarginPlatforms;
  amount: number;
  price: number | null;
  value: number;
  symbol: string;
  logoURI: string | undefined;
  baseDecimals: number;
}
type MarginUnsettledBalancesProps = {
  unsettledBalances: MarginPosition[];
  settleDisabled: boolean;
  onSettleAll: () => void;
};
export const MarginUnsettledBalances: FC<MarginUnsettledBalancesProps> = ({
  unsettledBalances,
  settleDisabled,
  onSettleAll,
}) => {
  const { priceInUSD } = useMarkets();
  const { watching } = useEnrichedWallet();

  const [pagination, setPagination] = useState<TablePaginationConfig>({});
  const [paginationSetting, setPaginationSetting] = useLocalStorageState(
    "marginUnsettledPaginationSizeSetting",
    PAGE_SIZE_OPTIONS.m[0],
  );

  const {
    currentLanguageData: {
      common,
      components: {
        table: { headers },
      },
      pages: {
        dashboard: {
          modules: {
            marginTrading: { marginUnsettledBalances: marginUnsettledBalancesText },
          },
        },
      },
    },
  } = useLanguageContext();

  const balances = unsettledBalances.map<UnsettledBalance>(({ mint, symbol, unsettled }) => {
    const price = priceInUSD(mint);
    const tokenInfo = getTokenBySymbol(symbol);
    const logoURI = getMarginPositionIcon(symbol);

    const amount = unsettled;
    return {
      kind: "Mango Markets",
      amount,
      price,
      value: amount * (price ?? 0),
      symbol,
      logoURI,
      baseDecimals: tokenInfo?.decimals || 6,
    };
  });
  const totalValue = balances.reduce((a, b) => (a += b.value), 0);
  useEffect(() => {
    setPagination({
      hideOnSinglePage: isPaginationHidden(balances.length, PAGE_SIZE_OPTIONS.m),
      showSizeChanger: true,
      pageSizeOptions: PAGE_SIZE_OPTIONS.m,
      pageSize: parseInt(paginationSetting),
    });
  }, [balances.length, paginationSetting]);

  const unsettledBalancesColumns: StepColumnsType<UnsettledBalance> = [
    {
      title: common.asset,
      key: "kind",
      dataIndex: "kind",
      render(_, record) {
        return (
          <AssetContainer>
            <TokenContainer>
              <URLIcon src={record.logoURI} />
              <TokenIconText bold>{record.symbol}</TokenIconText>
            </TokenContainer>
            <TokenTag
              icon={<URLIcon size={16} src={imageFromPlatformName(record.kind) ?? ""} />}
              text={getFirstWord(record.kind) ?? ""}
              uppercase={false}
            />
          </AssetContainer>
        );
      },
    },
    {
      title: common.amount,
      key: "amount",
      dataIndex: "amount",
      className: "column-number",
      sorter: (a, b) => a.amount - b.amount,
      render(amount: number, record) {
        return <CellValue cellType="number">{formatBalance(amount, record.baseDecimals)}</CellValue>;
      },
    },
    {
      title: headers.price,
      key: "price",
      dataIndex: "price",
      className: "column-number",
      sorter: (a, b) => (a.price ?? 0) - (b.price ?? 0),
      render(price: number) {
        return <CellValue cellType="value">{price}</CellValue>;
      },
    },
    {
      title: (
        <>
          {!watching && (
            <PriceContainer>
              <ContextButton disabled={settleDisabled} onClick={onSettleAll}>
                {marginUnsettledBalancesText.settleAllButton}
              </ContextButton>
              <PriceTitle>{common.value}</PriceTitle>
            </PriceContainer>
          )}
        </>
      ),
      key: "value",
      dataIndex: "value",
      className: "column-number",
      defaultSortOrder: "descend",
      sorter: (a, b) => a.value - b.value,
      render(value: number) {
        return (
          <CellValue cellType="userPositionValue" fontWeight="bold">
            {value}
          </CellValue>
        );
      },
    },
  ];
  return (
    <UnsettledBalancesContainer>
      <ChevronCollapse isCollapseChild title={marginUnsettledBalancesText.title} value={totalValue}>
        <StepTable
          rowKey={({ kind, symbol }) => kind + symbol}
          columns={unsettledBalancesColumns}
          key="margin-unsettled-table"
          dataSource={balances}
          pagination={pagination}
          onChange={(pagination) => {
            setPaginationSetting(pagination.pageSize ? pagination.pageSize.toString() : PAGE_SIZE_OPTIONS.m[0]);
          }}
        />
      </ChevronCollapse>
    </UnsettledBalancesContainer>
  );
};

const UnsettledBalancesContainer = styled.div`
  margin-top: 20px;
  .header {
    display: flex;
    justify-content: space-between;

    padding: 5px 14px;
  }
  .title {
    font-size: 14px;
    font-weight: 700;
    line-height: 22px;
    letter-spacing: 0em;
  }
  .totals {
    font-size: 14px;
    font-weight: 700;
    text-align: right;
  }
`;

const AssetContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const TokenContainer = styled(IconTextContainer)`
  display: flex;
  margin-right: 16px;
`;

const PriceTitle = styled.span`
  margin-left: 8px;
`;

const PriceContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
`;
